import {
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { DeliveryType } from "@1games-ru/region-shared/dist/enums/business/delivery";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { BoxIcon } from "@/shared/icons";
import Loader from "@/shared/ui/Loader";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import HeaderTitle from "@/shared/widgets/HeaderTitle";
import {
  goodsAndWarehouseCartListSelector,
  goodsAndWarehouseDataSelector,
  setGoodsAndWarehouseCartList,
} from "@/domains/business/slice";
import { getBusinessCardImage } from "@/domains/business/helpers/getBusinessImage";
import useGetGoodsAndWarehouseData from "@/domains/business/hooks/useGetGoodsAndWarehouseData";
import GoodsAndWarehouseCard from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseCard";
import GoodsAndWarehouseCartCard from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseCartCard";
import GoodsAndWarehouseCartFooter from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseCartFooter";
import GoodsAndWarehouseFooter from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseFooter";
import GoodsAndWarehouseGoodsWrapper from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseGoodsWrapper";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  onClose: () => void;
};

export default function GoodsAndWarehouseContainer({ info, onClose }: Props) {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isCart, setIsCart] = useState(false);
  // TODO тестовые данные для чекбокса, изменить когда появится логика на бэкенде |||||
  const [deliveryType, setDeliveryType] = useState<"default" | "express">("express");
  const [containerBounds, setContainerBounds] = useState<{ left: number; right: number }>({
    left: 0,
    right: 0,
  });

  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useAppSelector(goodsAndWarehouseDataSelector);
  const cartItemsSelector = useAppSelector(goodsAndWarehouseCartListSelector);
  const { isLoading, pay, isPaying } = useGetGoodsAndWarehouseData(info?.id);

  const cartItems = useMemo(() => cartItemsSelector || [], [cartItemsSelector]);
  const { totalCount, totalPrice } = useMemo(() => {
    if (!cartItems) return { totalCount: 0, totalPrice: 0 };

    return {
      totalCount: cartItems.reduce((acc, item) => acc + item.count, 0),
      totalPrice: cartItems.reduce((acc, item) => acc + item.price * item.count, 0),
    };
  }, [cartItems]);

  const clearCart = useCallback(() => {
    dispatch(setGoodsAndWarehouseCartList([]));
    setIsCart(false);
    setDeliveryType("express");
  }, [dispatch]);

  const handlePay = useCallback(() => {
    if (!cartItems.length) return;

    const materials = cartItems.map((item) => ({
      id: item.id,
      amount: item.count,
    }));

    pay(DeliveryType.Auto, materials, clearCart);
  }, [cartItems, clearCart, pay]);

  const handleCheckbox = () => {
    // TODO тестовые данные для чекбокса, изменить когда появится логика на бэкенде |||||
    setDeliveryType((prev) => (prev === "default" ? "express" : "default"));
  };

  const handleCountChange = useCallback(
    (id: number, count: number) => {
      dispatch(
        setGoodsAndWarehouseCartList(
          (() => {
            if (count <= 0) return cartItems?.filter((item) => item.id !== id);

            const existingItem = cartItems?.find((item) => item.id === id);
            const product = data?.materials?.find((item) => item.id === id);

            if (!existingItem && product) {
              return [...cartItems, { ...product, count }];
            }

            return cartItems?.map((item) => (item.id === id ? { ...item, count } : item));
          })(),
        ),
      );
    },
    [cartItems, data?.materials, dispatch],
  );

  const handleRemove = useCallback(
    (id: number) => {
      dispatch(setGoodsAndWarehouseCartList(cartItems.filter((item) => item.id !== id)));
    },
    [cartItems, dispatch],
  );

  const header = useMemo(
    () => (
      <HeaderTitle
        isOpen={isCart && cartItems.length > 0}
        title={t("business.goodsAndWarehouse.title")}
        titleOpen={t("business.goodsAndWarehouse.goodsCart")}
        onBack={() => setIsCart(false)}
      >
        <div className="flex items-center ml-auto mr-2.5">
          <BoxIcon className="size-3.5 text-white/30" />
          <p className="ml-2 text-11 font-bold leading-none uppercase">
            {t("business.goodsAndWarehouse.capacity")}:
          </p>
          <p className="flex items-center gap-1 h-8 ml-2.5 px-2.5 text-14 font-bold leading-none border border-[#191919] rounded-xl">
            <span className="text-accent-solid">{data?.warehouseCapacity || 0}</span> /{" "}
            {data?.maxWarehouseCapacity || 0}
            <span className="text-white/50">
              {t("business.goodsAndWarehouse.cnt")?.toLowerCase()}.
            </span>
          </p>
        </div>
      </HeaderTitle>
    ),
    [cartItems?.length, data?.maxWarehouseCapacity, data?.warehouseCapacity, isCart, t],
  );

  const footer = useMemo(
    () =>
      isCart && cartItems.length > 0 ? (
        <GoodsAndWarehouseCartFooter
          totalCount={totalCount}
          totalPrice={totalPrice}
          onPay={handlePay}
          isPaying={isPaying}
          handleClear={clearCart}
          deliveryType={deliveryType}
          setDeliveryType={handleCheckbox}
        />
      ) : null,
    [
      cartItems.length,
      clearCart,
      deliveryType,
      handlePay,
      isCart,
      isPaying,
      totalCount,
      totalPrice,
    ],
  );

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerBounds({ left: rect.left, right: rect.right });
    }
  }, []);

  return (
    <div className="relative flex flex-col w-full" ref={containerRef}>
      {isLoading && (
        <Loader className="top-[5rem] left-0 w-full h-[calc(100%-5rem)] p-0 bg-dark-900/70 translate-x-0 translate-y-0" />
      )}
      <DeviceTabContentWrapper header={header} footer={footer} onClose={onClose} className="pb-5">
        {isCart && cartItems.length > 0 ? (
          <div className="flex flex-col gap-2">
            {cartItems.map((item) => (
              <GoodsAndWarehouseCartCard
                key={item.id}
                item={item}
                handleCountChange={handleCountChange}
                handleRemove={handleRemove}
              />
            ))}
          </div>
        ) : (
          <GoodsAndWarehouseGoodsWrapper>
            {data?.materials?.map((item) => {
              const cartItem = cartItems?.find((ci) => ci.id === item.id);
              const currentQuantity = cartItem ? cartItem.count : 0;

              return (
                <GoodsAndWarehouseCard
                  key={item.id}
                  id={item.id}
                  title={item.name}
                  description={item.name}
                  price={item.price}
                  currentCount={item.warehouseAmount}
                  src={getBusinessCardImage(item.templateId, "materials")}
                  onCountChange={handleCountChange}
                  initialCount={currentQuantity}
                  containerBounds={containerBounds}
                />
              );
            })}
          </GoodsAndWarehouseGoodsWrapper>
        )}
      </DeviceTabContentWrapper>
      {!isLoading && !isCart && cartItems.length > 0 && (
        <GoodsAndWarehouseFooter
          totalCount={totalCount}
          totalPrice={totalPrice}
          setIsCart={setIsCart}
        />
      )}
    </div>
  );
}
